/**
 * International Timezone Support
 * 
 * Comprehensive IANA timezone list organized by region.
 * DST is automatically handled by the IANA timezone database.
 * 
 * Design decisions:
 * - Grouped by region for better UX
 * - Common timezones first within each region
 * - User-friendly labels (not raw IANA IDs)
 */

export interface TimezoneOption {
  value: string;  // IANA timezone identifier
  label: string;  // User-friendly display name
}

export interface TimezoneGroup {
  region: string;
  timezones: TimezoneOption[];
}

// Comprehensive international timezone list
export const TIMEZONE_GROUPS: TimezoneGroup[] = [
  {
    region: 'North America',
    timezones: [
      { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
      { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
      { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
      { value: 'America/Anchorage', label: 'Alaska' },
      { value: 'Pacific/Honolulu', label: 'Hawaii' },
      { value: 'America/Phoenix', label: 'Arizona (no DST)' },
      { value: 'America/Toronto', label: 'Toronto' },
      { value: 'America/Vancouver', label: 'Vancouver' },
      { value: 'America/Mexico_City', label: 'Mexico City' },
    ],
  },
  {
    region: 'South America',
    timezones: [
      { value: 'America/Sao_Paulo', label: 'São Paulo' },
      { value: 'America/Buenos_Aires', label: 'Buenos Aires' },
      { value: 'America/Santiago', label: 'Santiago' },
      { value: 'America/Lima', label: 'Lima' },
      { value: 'America/Bogota', label: 'Bogotá' },
      { value: 'America/Caracas', label: 'Caracas' },
    ],
  },
  {
    region: 'Europe',
    timezones: [
      { value: 'Europe/London', label: 'London, Dublin, Edinburgh' },
      { value: 'Europe/Paris', label: 'Paris, Brussels, Madrid' },
      { value: 'Europe/Berlin', label: 'Berlin, Amsterdam, Rome' },
      { value: 'Europe/Moscow', label: 'Moscow, St. Petersburg' },
      { value: 'Europe/Istanbul', label: 'Istanbul' },
      { value: 'Europe/Athens', label: 'Athens, Helsinki, Kyiv' },
      { value: 'Europe/Lisbon', label: 'Lisbon' },
      { value: 'Europe/Warsaw', label: 'Warsaw' },
      { value: 'Europe/Zurich', label: 'Zurich, Stockholm, Vienna' },
    ],
  },
  {
    region: 'Asia',
    timezones: [
      { value: 'Asia/Dubai', label: 'Dubai, Abu Dhabi' },
      { value: 'Asia/Kolkata', label: 'Mumbai, New Delhi, Kolkata' },
      { value: 'Asia/Singapore', label: 'Singapore' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
      { value: 'Asia/Shanghai', label: 'Beijing, Shanghai' },
      { value: 'Asia/Tokyo', label: 'Tokyo, Osaka' },
      { value: 'Asia/Seoul', label: 'Seoul' },
      { value: 'Asia/Bangkok', label: 'Bangkok, Jakarta' },
      { value: 'Asia/Manila', label: 'Manila' },
      { value: 'Asia/Karachi', label: 'Karachi, Islamabad' },
      { value: 'Asia/Tehran', label: 'Tehran' },
      { value: 'Asia/Jerusalem', label: 'Jerusalem, Tel Aviv' },
      { value: 'Asia/Riyadh', label: 'Riyadh' },
    ],
  },
  {
    region: 'Australia & Pacific',
    timezones: [
      { value: 'Australia/Sydney', label: 'Sydney, Melbourne' },
      { value: 'Australia/Brisbane', label: 'Brisbane (no DST)' },
      { value: 'Australia/Perth', label: 'Perth' },
      { value: 'Australia/Adelaide', label: 'Adelaide' },
      { value: 'Pacific/Auckland', label: 'Auckland, Wellington' },
      { value: 'Pacific/Fiji', label: 'Fiji' },
      { value: 'Pacific/Guam', label: 'Guam' },
    ],
  },
  {
    region: 'Africa',
    timezones: [
      { value: 'Africa/Cairo', label: 'Cairo' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg, Cape Town' },
      { value: 'Africa/Lagos', label: 'Lagos' },
      { value: 'Africa/Nairobi', label: 'Nairobi' },
      { value: 'Africa/Casablanca', label: 'Casablanca' },
    ],
  },
  {
    region: 'Atlantic',
    timezones: [
      { value: 'Atlantic/Reykjavik', label: 'Reykjavik (no DST)' },
      { value: 'Atlantic/Azores', label: 'Azores' },
      { value: 'Atlantic/Cape_Verde', label: 'Cape Verde' },
    ],
  },
];

// Flat list for simple dropdowns
export const ALL_TIMEZONES: TimezoneOption[] = TIMEZONE_GROUPS.flatMap(g => g.timezones);

// Default timezone
export const DEFAULT_TIMEZONE = 'America/New_York';

// Get label from timezone value
export function getTimezoneLabel(value: string): string {
  for (const group of TIMEZONE_GROUPS) {
    const tz = group.timezones.find(t => t.value === value);
    if (tz) return tz.label;
  }
  return value; // Fallback to IANA ID
}

// Get timezone abbreviation (e.g., "EST", "PST")
export function getTimezoneAbbreviation(value: string, date: Date = new Date()): string {
  try {
    return date.toLocaleString('en-US', { timeZone: value, timeZoneName: 'short' })
      .split(' ')
      .pop() || '';
  } catch {
    return '';
  }
}

/**
 * Get the UTC offset in minutes for a timezone at a specific UTC moment.
 * Positive = east of UTC, negative = west of UTC.
 * Used for computing display offsets between two timezones.
 */
export function utcOffsetMinutes(timezone: string, utcDate: Date = new Date()): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(utcDate);
  const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';
  const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2]) * 60 + parseInt(match[3] || '0'));
}

// Format time in timezone
export function formatInTimezone(
  date: Date | string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {}
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  try {
    return d.toLocaleString('en-US', { timeZone: timezone, ...options });
  } catch {
    return d.toLocaleString('en-US', { timeZone: DEFAULT_TIMEZONE, ...options });
  }
}
